import { useState } from "react";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2, RotateCw, Image, ImagePlus, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Column } from "@/components/tables/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useGetCarouselsQuery,
  useCreateCarouselMutation,
  useUpdateCarouselMutation,
  useDeleteCarouselMutation,
  Carousel
} from "@/redux/slices/carouselApi";
import { useUploadImageMutation } from "@/redux/slices/uploadApi";
import { getFileUrl } from "@/lib/utils";

const schema = z.object({
  header: z.string().trim().min(2, "Header required").max(60),
  text: z.string().trim().min(5, "Subtext/Description required").max(200),
  imageUrl: z.string().min(1, "Banner image is required"),
  route: z.string().min(1, "Navigation route is required"),
  isActive: z.boolean(),
  order: z.preprocess((val) => Number(val), z.number().min(0, "Order must be >= 0")),
});
type FormValues = z.infer<typeof schema>;

const APP_ROUTES = [
  { label: "Home Screen", value: "/(drawer)/(tabs)/home" },
  { label: "Subjects Screen", value: "/(drawer)/(tabs)/subjects" },
  { label: "Notes Screen", value: "/(drawer)/(tabs)/notes" },
  { label: "Exams Screen", value: "/(drawer)/(tabs)/exam" },
  { label: "About Us Screen", value: "/(drawer)/about" },
  { label: "Favourites Screen", value: "/(drawer)/favourites" },
  { label: "Student Feedback Screen", value: "/(drawer)/feedback" },
  { label: "Premium Plans", value: "/premium" },
  { label: "Help & Support", value: "/help" },
  { label: "Privacy Policy", value: "/privacy" },
  { label: "Security Settings", value: "/security" },
  { label: "Data & Cache Settings", value: "/data-usage" },
];

export default function CarouselManagementPage() {
  const { data, isFetching, refetch } = useGetCarouselsQuery();
  const [addCarousel, addM] = useCreateCarouselMutation();
  const [updateCarousel, updateM] = useUpdateCarouselMutation();
  const [deleteCarousel, deleteM] = useDeleteCarouselMutation();
  const [uploadImage, uploadM] = useUploadImageMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Carousel | null>(null);
  const [toDelete, setToDelete] = useState<Carousel | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { header: "", text: "", imageUrl: "", route: "", isActive: true, order: 0 },
  });

  const carouselItems = data?.data || [];
  const maxLimitReached = carouselItems.length >= 5;

  const openCreate = () => {
    if (maxLimitReached) {
      toast.error("You can add a maximum of 5 carousels only.");
      return;
    }
    setEditing(null);
    form.reset({ header: "", text: "", imageUrl: "", route: "/(drawer)/(tabs)/home", isActive: true, order: 0 });
    setOpen(true);
  };

  const openEdit = (c: Carousel) => {
    setEditing(c);
    form.reset({
      header: c.header,
      text: c.text,
      imageUrl: c.imageUrl,
      route: c.route || "/(drawer)/(tabs)/home",
      isActive: c.isActive,
      order: c.order,
    });
    setOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await uploadImage({ formData, folder: "carousels", subfolder: "banners" }).unwrap();
      form.setValue("imageUrl", res.path);
      toast.success("Banner image uploaded successfully");
    } catch {
      toast.error("Upload failed");
    }
  };

  const onSubmit = async (v: FormValues) => {
    try {
      if (editing) {
        await updateCarousel({ id: editing.id, body: v }).unwrap();
        toast.success("Carousel banner updated successfully");
      } else {
        await addCarousel(v).unwrap();
        toast.success("Carousel banner added successfully");
      }
      setOpen(false);
    } catch (err: any) {
      const errMsg = err?.data?.message || "Something went wrong";
      toast.error(errMsg);
    }
  };

  const getRouteLabel = (routeVal: string) => {
    const routeObj = APP_ROUTES.find(r => r.value === routeVal);
    return routeObj ? routeObj.label : routeVal;
  };

  const columns: Column<Carousel>[] = [
    {
      key: "imageUrl",
      header: "Banner Image",
      render: (r) => (
        <div className="relative flex h-14 w-28 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-muted shadow-sm transition-transform hover:scale-105">
          {r.imageUrl ? (
            <img src={getFileUrl(r.imageUrl)} alt={r.header} className="h-full w-full object-cover" />
          ) : (
            <Image className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
      )
    },
    {
      key: "header",
      header: "Header & Text",
      render: (r) => (
        <div className="flex flex-col max-w-sm">
          <span className="font-semibold text-foreground truncate">{r.header}</span>
          <span className="text-xs text-muted-foreground truncate">{r.text}</span>
        </div>
      )
    },
    {
      key: "route",
      header: "Redirect Destination",
      render: (r) => (
        <span className="inline-flex items-center rounded-md bg-secondary/80 px-2 py-1 text-xs font-semibold text-secondary-foreground shadow-sm">
          {getRouteLabel(r.route)}
        </span>
      )
    },
    {
      key: "order",
      header: "Sort Order",
      render: (r) => <span className="font-mono text-sm font-medium">{r.order}</span>
    },
    {
      key: "isActive",
      header: "Status",
      render: (r) => <StatusBadge status={r.isActive ? "active" : "inactive"} />
    }
  ];

  return (
    <div className="min-h-screen bg-muted/20">
      <PageHeader
        title="Home Screen Carousels"
        description="Manage dynamic banners and call-to-action buttons for the mobile app home screen."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
              <RotateCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
            <Button 
              onClick={openCreate} 
              disabled={maxLimitReached}
              className="shadow-lg shadow-primary/20"
            >
              <Plus className="mr-1.5 h-4 w-4" /> Add Banner
            </Button>
          </div>
        }
      />
      
      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        {maxLimitReached && (
          <Alert className="border-amber-500/30 bg-amber-500/5 text-amber-600 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-500 rounded-2xl">
            <Info className="h-5 w-5 !text-amber-500" />
            <AlertTitle className="font-bold">Maximum limit of 5 carousels reached</AlertTitle>
            <AlertDescription className="text-sm font-medium">
              You have active 5/5 carousel items. To add a new one, please delete or edit one of the existing items.
            </AlertDescription>
          </Alert>
        )}

        <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
          <DataTable
            columns={columns}
            rows={carouselItems}
            loading={isFetching}
            total={carouselItems.length}
            actions={(row) => (
              <div className="flex justify-end gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(row)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setToDelete(row)} className="text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          />
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg shadow-xl border-none p-0 overflow-hidden rounded-xl flex flex-col max-h-[85vh]">
          <DialogHeader className="p-6 pb-4 bg-primary/5 text-left shrink-0">
            <DialogTitle className="text-xl font-bold">{editing ? "Edit Carousel Banner" : "Create Dynamic Banner"}</DialogTitle>
            <DialogDescription className="font-medium text-sm text-muted-foreground">Configure the banner visual details and destination route.</DialogDescription>
          </DialogHeader>
          
          <div className="px-6 py-4 flex-1 overflow-y-auto">
            <form id="carousel-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="header" className="text-sm font-semibold text-foreground">Header Text (Title)</Label>
                <Input id="header" className="h-11 shadow-sm" placeholder="e.g. Special Offer: 20% Off" {...form.register("header")} />
                {form.formState.errors.header && (
                  <p className="text-xs font-medium text-destructive">{form.formState.errors.header.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="text" className="text-sm font-semibold text-foreground">Body Text (Description)</Label>
                <Textarea id="text" className="shadow-sm min-h-[80px]" placeholder="e.g. Master Tridosha theory easily with our latest comprehensive guide." {...form.register("text")} />
                {form.formState.errors.text && (
                  <p className="text-xs font-medium text-destructive">{form.formState.errors.text.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">Redirection Route</Label>
                  <Select
                    value={form.watch("route")}
                    onValueChange={(v) => form.setValue("route", v)}
                  >
                    <SelectTrigger className="h-11 shadow-sm">
                      <SelectValue placeholder="Select Screen Route" />
                    </SelectTrigger>
                    <SelectContent>
                      {APP_ROUTES.map(route => (
                        <SelectItem key={route.value} value={route.value}>{route.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.formState.errors.route && (
                    <p className="text-xs font-medium text-destructive">{form.formState.errors.route.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="order" className="text-sm font-semibold text-foreground">Display Order Weight</Label>
                  <Input id="order" type="number" className="h-11 shadow-sm font-mono" placeholder="0" {...form.register("order")} />
                  {form.formState.errors.order && (
                    <p className="text-xs font-medium text-destructive">{form.formState.errors.order.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-3 rounded-xl border bg-muted/10 p-5 shadow-sm">
                <Label className="text-xs font-bold text-primary uppercase tracking-wider">Banner Visuals</Label>
                <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                  <div className="relative group flex h-24 w-48 shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-primary/20 bg-background shadow-sm transition-all hover:border-primary/50">
                    {form.watch("imageUrl") ? (
                      <img src={getFileUrl(form.watch("imageUrl")!)} className="h-full w-full object-cover" />
                    ) : (
                      <ImagePlus className="h-6 w-6 text-muted-foreground/40" />
                    )}
                    {uploadM.isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                        <Loader2 className="h-6 w-6 animate-spin text-white" />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="absolute inset-0 cursor-pointer opacity-0"
                      onChange={handleImageUpload}
                      disabled={uploadM.isLoading}
                    />
                  </div>
                  <div className="flex-1 space-y-1.5 mt-1">
                    <Label className="text-xs font-semibold text-foreground">Upload Image</Label>
                    <p className="text-[10px] text-muted-foreground leading-snug">
                      Click the box on the left to upload a beautiful background image for your carousel slide. Best resolution: 800x400 pixels.
                    </p>
                  </div>
                </div>
                {form.formState.errors.imageUrl && (
                  <p className="text-xs font-medium text-destructive">{form.formState.errors.imageUrl.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Active Status</Label>
                <Select
                  value={form.watch("isActive") ? "true" : "false"}
                  onValueChange={(v) => form.setValue("isActive", v === "true")}
                >
                  <SelectTrigger className="h-11 shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active (Visible to students)</SelectItem>
                    <SelectItem value="false">Inactive (Hidden from students)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </form>
          </div>
          
          <DialogFooter className="border-t p-6 bg-muted/5 shrink-0">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-10 px-4 text-sm font-medium">Cancel</Button>
            <Button form="carousel-form" type="submit" disabled={addM.isLoading || updateM.isLoading} className="h-10 px-6 rounded-md text-sm font-medium shadow-md transition-all">
              {editing ? "Update Banner" : "Create Banner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          await deleteCarousel(toDelete.id).unwrap();
          toast.success("Carousel banner permanently removed");
          setToDelete(null);
        }}
        loading={deleteM.isLoading}
        title="Remove Carousel Slide?"
        description={`The banner "${toDelete?.header}" will be permanently removed and students will no longer see it on the home screen.`}
      />
    </div>
  );
}
